import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MessageSquare, Send } from "lucide-react";
import { fmtDateTime } from "@/lib/dateFormat";

/**
 * CaseCommunicationLog
 *
 * Right-column "Supporting Intelligence · Case Context" panel that surfaces
 * the conversation thread between PM, NTN staff, and applicant on a
 * screening. Replaces the legacy bottom-of-page chat thread per platform spec.
 *
 * Props:
 *  - messages: list of message objects { id, sender_id, sender_name, sender_role, content, created_at }
 *  - currentUserId: id of the viewing user (drives left/right bubble alignment)
 *  - onSend(content): async handler invoked when the form is submitted with a non-empty input
 */
export default function CaseCommunicationLog({ messages, currentUserId, onSend }) {
  const [msgInput, setMsgInput] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = msgInput.trim();
    if (!trimmed) return;
    await onSend(trimmed);
    setMsgInput("");
  };

  return (
    <section className="bg-white rounded-lg ring-1 ring-slate-200 shadow-sm overflow-hidden" data-testid="case-communication-log">
      <header className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2.5">
        <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
          <MessageSquare className="h-4 w-4 text-slate-600" />
        </div>
        <div className="min-w-0">
          <div className="ntn-eyebrow">Supporting Intelligence · Case Context</div>
          <div className="ntn-h3 mt-0.5">Case Communication Log</div>
        </div>
      </header>
      <div className="flex flex-col" style={{ maxHeight: 480 }}>
        <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[240px]">
          {messages.length === 0 ? (
            <div className="ntn-secondary text-center py-10">
              No communication on file yet. Notes, questions, and updates added here become part of the applicant record.
            </div>
          ) : messages.map((m) => {
            const mine = m.sender_id === currentUserId;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                <div className={`max-w-[85%] rounded-md p-3 ring-1 ${mine ? "bg-slate-900 text-white ring-slate-900" : "bg-slate-50 text-slate-900 ring-slate-200"}`}>
                  <div className={`ntn-eyebrow mb-1 ${mine ? "text-white/70" : ""}`}>
                    {m.sender_name} · {m.sender_role.replace("_", " ")}
                    {m.kind === "note" && (
                      <span className={mine ? "text-white/70" : "text-slate-500"}>
                        {" "}· NTN Note{m.visibility === "staff_only" ? " (staff only)" : ""}
                      </span>
                    )}
                  </div>
                  <div className="ntn-body whitespace-pre-wrap leading-relaxed" style={mine ? { color: "white" } : undefined}>{m.content}</div>
                  <div className={`ntn-mono mt-1 ${mine ? "text-white/60" : ""}`}>{fmtDateTime(m.created_at)}</div>
                </div>
              </div>
            );
          })}
        </div>
        <form onSubmit={handleSubmit} className="p-3 border-t border-slate-100 flex gap-2 bg-slate-50/40" data-testid="message-form">
          <Input value={msgInput} onChange={(e) => setMsgInput(e.target.value)}
                 placeholder="Add a note, question, or update…"
                 className="rounded-md text-sm" data-testid="message-input" />
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-md shrink-0" data-testid="send-message-btn">
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
      <footer className="px-5 py-2.5 border-t border-slate-100 bg-slate-50/60">
        <p className="ntn-caption leading-snug">
          Case context. Notes added here are part of the applicant intelligence record.
        </p>
      </footer>
    </section>
  );
}
